import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    useTheme,
    Alert,
    Link,
    InputAdornment,
    IconButton,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const API_URL = 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

const ResetPasswordPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        // Pre-fill email from forgot password page
        const savedEmail = sessionStorage.getItem('resetEmail');
        if (savedEmail) {
            setEmail(savedEmail);
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        // Validate password strength
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
            setError('Password must contain uppercase, lowercase, and numbers');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, code, newPassword }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSuccess(true);
                sessionStorage.removeItem('resetEmail');
                // Redirect to login after 2 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                // Display the specific error message from backend
                setError(data.error || 'Failed to reset password');
            }
        } catch (err: any) {
            // Only show generic network error if fetch itself fails
            console.error('Reset password network error:', err);
            setError('Unable to connect to server. Please check your internet connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
            }}
        >
            <Container maxWidth="sm">
                <Card sx={{ borderRadius: '16px' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    border: `3px solid ${theme.palette.primary.main}`,
                                    backgroundColor: 'transparent',
                                    mb: 2,
                                }}
                            >
                                <LockResetIcon sx={{ fontSize: '2.5rem', color: theme.palette.primary.main }} />
                            </Box>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                Reset Password
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                                Enter the code from your email and your new password
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                                {error}
                            </Alert>
                        )}

                        {success ? (
                            <Alert severity="success" sx={{ mb: 3, borderRadius: '8px' }}>
                                Password reset successfully! Redirecting to login...
                            </Alert>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    sx={{ mb: 3 }}
                                />

                                <TextField
                                    fullWidth
                                    label="Verification Code"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    required
                                    placeholder="Enter 6-digit code"
                                    sx={{ mb: 3 }}
                                />

                                <TextField
                                    fullWidth
                                    label="New Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    sx={{ mb: 3 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    helperText="8+ characters with uppercase, lowercase, and numbers"
                                />

                                <TextField
                                    fullWidth
                                    label="Confirm New Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    sx={{ mb: 3 }}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={loading}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: '30px',
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    {loading ? 'Resetting...' : 'Reset Password'}
                                </Button>
                            </form>
                        )}

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: 'text.secondary' }}>
                                Didn't receive a code?{' '}
                                <Link
                                    href="/forgot-password"
                                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    Send Again
                                </Link>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default ResetPasswordPage;
